// We'll reuse the styles from the text-input component, since we'll be sharing with that.
import React, { useLayoutEffect, useRef } from "react";
import TextareaAutosize from "react-autosize-textarea";
import { FieldWrapper } from "@montana/shared/form/field-wrapper";

import { ISharedFieldProperties } from "../field/field";
import { withForm } from "../field";
import { isFieldTouched, isFieldValid } from "../field/field.component";
import { isBrowser } from "@montana/helpers/browser.helper";

type ITextAreaProps = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "cols" | "rows"> & ISharedFieldProperties


export const TextArea: React.FC<ITextAreaProps> = React.memo((props: ITextAreaProps): JSX.Element => {
    const {
        isValid = true,
        label,
        className,
        errorMessage,
        isTouched,
        floatingLabel,
        autoFocus,
        autoFocusDelay,
        ...fieldProps
    } = props;
    const { id } = props;

    const textareaElement = useRef<HTMLTextAreaElement | null>();

    const useDynamicEffect = isBrowser ? useLayoutEffect : (): void => {/* Do nothing. */};

    useDynamicEffect(() => {
        if (autoFocus) {
            if (autoFocusDelay) {
                setTimeout(() => textareaElement.current && textareaElement.current.focus(), autoFocusDelay);
            } else {
                textareaElement.current && textareaElement.current.focus();
            }
        }
    }, []);

    return (
        <FieldWrapper
            label={label || ""}
            id={id || ""}
            errorMessage={errorMessage}
            isEmpty={props.value === ""}
            isValid={isValid}
            isTouched={isTouched}
            typeName="textarea"
            floatingLabel={floatingLabel}
            dontRenderLabel={!label}
        >
            <TextareaAutosize ref={textareaElement as any} className={`text-input text-input--multiline${className ? ` ${className}` : ''}${floatingLabel ? " floating-label__input" : ''} `} {...fieldProps} />
        </FieldWrapper>
    );
});

export const FormTextArea = withForm<ITextAreaProps, 'onChange' | 'isValid'>(TextArea, (field, form): ITextAreaProps => ({
    onChange: form.handleChange,
    onBlur: form.handleBlur,
    value: field.value,
    errorMessage: (form.errors as { [key: string]: string })[field.name],
    isValid: isFieldValid(field.name, form),
    isTouched: isFieldTouched(field.name, form),
}));
